import React from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import styles from './ActionButton.module.scss';

export enum ButtonType {
  SYSTEM = 'system',
  ACTION = 'action',
  ACTION_SECONDARY = 'action-secondary',
  CANCEL = 'cancel',
  SYSTEM_PRIMARY = 'system-primary',
  SYSTEM_SECONDARY = 'system-secondary',
  PARTICIPATION = 'participation',
  HOME = 'home',
}

interface Props {
  children?: React.ReactNode;
  onClick?: () => void;
  type: ButtonType;
  href?: string;
  target?: string;
  className?: string;
  disabled?: boolean;
  action?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  title?: string;
}

function buildClassName(type: ButtonType, className?: string): string {
  return classNames(styles.button, className, {
    [styles.system]: type === ButtonType.SYSTEM,
    [styles.action]: type === ButtonType.ACTION,
    [styles['action-secondary']]: type === ButtonType.ACTION_SECONDARY,
    [styles.cancel]: type === ButtonType.CANCEL,
    [styles['system-primary']]: type === ButtonType.SYSTEM_PRIMARY,
    [styles['system-secondary']]: type === ButtonType.SYSTEM_SECONDARY,
    [styles.participation]: type === ButtonType.PARTICIPATION,
    [styles.home]: type === ButtonType.HOME,
  });
}

export function ActionButton(props: Props) {
  const { children, onClick, type, href, target, className, disabled, action, ariaLabel, title } = props;
  const combinedClassName = buildClassName(type, className);

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        aria-label={ariaLabel}
        className={combinedClassName}
        onClick={target === '_blank' ? onClick : undefined}
        title={title}
      >
        {children}
      </Link>
    );
  }
  // An explicit action (submit/reset) is interactive even without a click handler:
  // the enclosing form handles it.
  if (onClick || action) {
    return (
      <button
        className={combinedClassName}
        // eslint-disable-next-line react/button-has-type
        type={action || 'button'}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
      >
        {children}
      </button>
    );
  }
  return (
    <div className={combinedClassName} aria-label={ariaLabel} title={title}>
      {children}
    </div>
  );
}
